/*
 * Datazoom, Inc ("COMPANY") CONFIDENTIAL
 * Copyright (c) 2017-2018 [Datazoom, Inc.], All Rights Reserved.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 */

import axios from "axios";
import download from "downloadjs";
import moment from "moment";
import {
  aggregationApi,
  baseApiV1,
  baseApiV2,
  getRatesApi,
  getStandadRatesApi,
  setCustomRatesApi,
  setStandardRatesApi,
  updateCustomRatesApi,
  updateStandardRatesApi,
  creditHistoryApi,
  creditAdjustmentApi,
  billingHistoryApi,
} from "../../constants/Presets";
import { formatDate } from "./helper";
import { ORDER_DESC } from "../../constants/Constants";
import { getYYYMMDD } from "../../app-old/utilities/utils";
// function that makes the api request and returns a Promise for response

/* istanbul ignore next */
export function FetchCurrentBill(payload) {
  const { companyId } = payload;
  const { isMonthly } = payload;
  const starDate = payload.startDate;
  const { endDate } = payload;

  const cookieCompanyUuid = companyId;
  return axios.get(
    `${aggregationApi}/dataUsage/currentBillingPeriod/${formatDate(
      starDate,
    )}/${formatDate(endDate)}`,
    {
      params: {
        isMonthly,
        companyId,
      },
    },
  );
}
/* istanbul ignore next */
export function FetchCurrentBreakDown(payload) {
  const { companyId } = payload;
  /* istanbul ignore if */
  if (payload.breakDownScale === "y") {
    const { isMonthly } = payload;
    return axios.get(
      `${aggregationApi}/dataUsage/breakdown/${formatDate(
        payload.startDate,
      )}/${formatDate(payload.endDate)}/${isMonthly}`,
      {
        params: {
          companyId,
        },
      },
    );
  }
}
/* istanbul ignore next */
export function FetchCountSummary(customerCode) {
  let summaryUrl = `${baseApiV2}company/dashboard/summary`;
  /* istanbul ignore if */
  if (customerCode) {
    summaryUrl += `?company_id=${customerCode}`;
  }
  return axios.get(summaryUrl);
}
/* istanbul ignore next */
export function FetchPreviousBreakDown(payload) {
  const { companyId } = payload;
  /* istanbul ignore if */
  if (payload.breakDownScale === "y") {
    const currDate = new Date();
    const period = 3;
    const startDate = getYYYMMDD(
      new Date(currDate.getFullYear(), currDate.getMonth(), 0),
    );
    const endDate = getYYYMMDD(
      new Date(currDate.getFullYear(), currDate.getMonth() - period, 1),
    );
    const { isMonthly } = payload;
    return axios.get(
      `${aggregationApi}/dataUsage/breakdown/${formatDate(
        endDate,
      )}/${formatDate(currDate)}/${isMonthly}`,
      {
        params: {
          companyId,
        },
      },
    );
  }
}
/* istanbul ignore next */
export function FetchTotalBreak(payload) {
  const { companyId } = payload;
  const { isMonthly } = payload;
  const endDate = moment(payload.endDate).endOf("month").format("YYYY-MM-DD");
  return axios.get(
    `${aggregationApi}/dataUsage/totalUsage/${payload.startDate}/${endDate}/${isMonthly}`,
    {
      params: {
        companyId,
      },
    },
  );
}
/* istanbul ignore next */
export function FetchAllRates(payload) {
  payload = payload || {};
  const url = `${getRatesApi}`;
  const params = [];
  /* istanbul ignore if */
  if (payload.query) {
    params.push(`q=${payload.query}`);
  }
  const field = payload.field || "updated_on";
  params.push(`field=${field}`);
  const order = payload.order ? payload.order : ORDER_DESC;
  params.push(`order=${order}`);
  const page = payload.page || 0;
  params.push(`page=${page}`);
  const size = payload.size || 10;
  params.push(`size=${size}`);
  /* istanbul ignore if */
  if (payload.customerCode) {
    const customerCode = payload.customerCode || "";
    params.push(`companyId=${customerCode}`);
  }
  /* istanbul ignore if */
  if (payload.startDate) {
    const startDate = payload.startDate || "";
    params.push(`startDate=${startDate}`);
  }
  /* istanbul ignore if */
  if (payload.endDate) {
    const endDate = payload.endDate || "";
    params.push(`endDate=${endDate}`);
  }
  const paramStr = params.join("&");
  return axios.get(url + paramStr);
}
/* istanbul ignore next */
export function FetchAllStandardRates(payload) {
  payload = payload || {};
  const url = `${getStandadRatesApi}`;
  const params = [];
  /* istanbul ignore if */
  if (payload.query) {
    params.push(`q=${payload.query}`);
  }
  const field = payload.field || "updated_on";
  params.push(`field=${field}`);
  const order = payload.order ? payload.order : ORDER_DESC;
  params.push(`order=${order}`);
  const page = payload.page || 0;
  params.push(`page=${page}`);
  const size = payload.size || 10;
  params.push(`size=${size}`);
  /* istanbul ignore if */
  if (payload.startDate) {
    const startDate = payload.startDate || "";
    params.push(`startDate=${startDate}`);
  }
  /* istanbul ignore if */
  if (payload.endDate) {
    const endDate = payload.endDate || "";
    params.push(`endDate=${endDate}`);
  }
  const paramStr = params.join("&");
  return axios.get(url + paramStr);
}
/* istanbul ignore next */
export function SetRate(rate) {
  const url = `${setCustomRatesApi}`;
  const params = {
    displayName: rate.displayName,
    companyId: rate.customerCode,
    startDate: rate.startDate,
    endDate: rate.endDate,
    rates: rate.rates,
  };
  return axios.post(url, params);
}
/* istanbul ignore next */
export function SetStandardRate(rate) {
  const url = `${setStandardRatesApi}`;
  const params = {
    displayName: rate.displayName,
    startDate: rate.startDate,
    endDate: rate.endDate,
    rates: rate.rates,
  };
  return axios.post(url, params);
}
/* istanbul ignore next */
export function EditCustomRate(rate) {
  const url = `${updateCustomRatesApi}`;
  const params = {
    displayName: rate.displayName,
    customRateId: rate.id,
    companyId: rate.customerCode,
    startDate: rate.startDate,
    endDate: rate.endDate,
    rates: rate.rates,
  };
  return axios.patch(url, params);
}
/* istanbul ignore next */
export function EditStandardRate(rate) {
  const url = `${updateStandardRatesApi}`;
  const params = {
    displayName: rate.displayName,
    standardRateId: rate.id,
    startDate: rate.startDate,
    endDate: rate.endDate,
    rates: rate.rates,
  };
  return axios.patch(url, params);
}
/* istanbul ignore next */
export function getCreditHistory(customerCode) {
  return axios.get(`${creditHistoryApi}?companyId=${customerCode}`);
}
/* istanbul ignore next */
export function getBillingHistory(customerCode) {
  return axios.get(`${billingHistoryApi}?companyId=${customerCode}`);
}
/* istanbul ignore next */
export function downloadInvoice(invoiceId) {
  let invoiceUrl = `${baseApiV2}billing/invoice`;
  /* istanbul ignore if */
  if (invoiceId) {
    invoiceUrl += `?invoiceId=${invoiceId}`;
  }
  return axios(invoiceUrl, {
    method: "POST",
    responseType: "blob",
  }).then((response) => {
    const content = response.headers["content-type"];
    download(response.data, "file".pdf, content);
    return response;
  });
}
/* istanbul ignore next */
export function creditAdjustment(credit) {
  const url = `${creditAdjustmentApi}`;
  const params = {
    companyId: credit.customerCode,
    description: credit.explanation,
    creditAdjustment: credit.creditAdjustment,
    unit: credit.unitRate,
  };
  return axios.post(url, params);
}
/* istanbul ignore next */
export function FetchStandardRatesList(payload) {
  payload = payload || {};
  const url = `${baseApiV2}rate_card/standard/list?`;
  const params = [];
  const page = payload.page || 0;
  params.push(`page=${page}`);
  const size = payload.size || 100;
  params.push(`page_size=${size}`);
  const { sortBy } = payload;
  params.push(`sort_by=${sortBy}`);
  const sortOrder = payload.sortOrder ? payload.sortOrder : "desc";
  params.push(`sort_order=${sortOrder}`);
  const paramStr = params.join("&");
  return axios.get(url + paramStr);
}
/* istanbul ignore next */
export function FetchAStandardRate(id) {
  return axios.get(`${baseApiV2}rate_card/standard?id=${id}`);
}
/* istanbul ignore next */
export function DeleteStandardRate(id) {
  return axios.delete(`${baseApiV2}rate_card/standard?id=${id}`);
}
/* istanbul ignore next */
export function FetchCustomRatesList(payload) {
  payload = payload || {};
  const url = `${baseApiV2}rate_card/custom/list?`;
  const params = [];
  const page = payload.page || 0;
  params.push(`page=${page}`);
  const size = payload.size || 100;
  params.push(`page_size=${size}`);
  const { sortBy } = payload;
  params.push(`sort_by=${sortBy}`);
  const sortOrder = payload.sortOrder ? payload.sortOrder : "desc";
  params.push(`sort_order=${sortOrder}`);
  const companyId = payload.companyId || "";
  /* istanbul ignore if */
  if (payload.companyId) {
    params.push(`company_id=${companyId}`);
  }
  const paramStr = params.join("&");
  return axios.get(url + paramStr);
}
/* istanbul ignore next */
export function CreateStandardRate(rate) {
  const url = `${baseApiV2}rate_card/standard`;
  const params = {
    startDate: rate.startDate,
    rate: rate.rate,
    enrichmentTiers: rate.enrichmentTiers,
  };
  return axios.post(url, params);
}
export function FetchACustomRate(id) {
  return axios.get(`${baseApiV2}rate_card/custom?id=${id}`);
}
export function CreateCustomRate(rate) {
  const url = `${baseApiV2}rate_card/custom`;
  const params = {
    startDate: rate.startDate,
    rate: rate.rate,
    enrichmentTiers: rate.enrichmentTiers,
    companyId: rate.companyId,
    platformFee: rate.platformFee,
    paymentType: rate.paymentType,
    useStandardEnrichmentTiers: rate.useStandardEnrichmentTiers,
  };
  return axios.post(url, params);
}
/* istanbul ignore next */
export function FetchCurrentStandardRate() {
  return axios.get(`${baseApiV2}rate_card/standard/current`);
}
/* istanbul ignore next */
export function DeleteCustomRate(id) {
  return axios.delete(`${baseApiV2}rate_card/custom?id=${id}`);
}
/* istanbul ignore next */
export function FetchACustomRateStatus(payload) {
  const { company_id } = payload;
  return axios.get(`${baseApiV2}rate_card/custom/${payload.status}`, {
    params: {
      company_id,
    },
  });
}
/* istanbul ignore next */
export function FetchAStandardRateStatus(payload) {
  return axios.get(`${baseApiV2}rate_card/standard/${payload.status}`);
}

export function FetchMediatailorDataUsage(payload) {
  payload = payload || {};
  const url = `${baseApiV1}dashboard/mediatailor/app_session_logs?`;
  const params = [];
  params.push(`company_id=${payload?.company_id}`);
  params.push(`from_date=${payload?.from_date}`);
  params.push(`to_date=${payload?.to_date}`);
  const paramStr = params.join("&");
  return axios.get(url + paramStr);
}
